/**
 * Storage Keys - Centralized localStorage key management
 *
 * Implements the key naming convention from docs/specs/UI_STANDARDS.md:
 * - Drawer state keys: openscad-drawer-{name}-state
 * - Drawer size keys: openscad-drawer-{name}-width or -height
 * - App preferences: openscad-forge-{feature}
 *
 * Provides one-time migration from legacy key patterns.
 *
 * @license GPL-3.0-or-later
 */

// Migration version - increment when adding new migrations
const MIGRATION_VERSION = 1;
const MIGRATION_KEY = 'openscad-forge-storage-migration-version';

/**
 * Key mapping from legacy patterns to standardized patterns
 * Format: { legacy: new, ... }
 *
 * Categorized by:
 * - DRAWER: openscad-drawer-{name}-state
 * - APP: openscad-forge-{feature}
 */
export const KEY_MIGRATIONS = {
  // ============================================================================
  // Drawer State Keys -> openscad-drawer-{name}-state
  // ============================================================================
  'openscad-customizer-param-panel-collapsed':
    'openscad-drawer-parameters-state',
  'openscad-customizer-camera-panel-collapsed': 'openscad-drawer-camera-state',
  'openscad-customizer-camera-drawer-collapsed':
    'openscad-drawer-camera-mobile-state',
  'openscad-customizer-drawer-collapsed':
    'openscad-drawer-preview-settings-state',
  // Note: 'openscad-drawer-actions-state' already follows spec

  // Preview camera controls (not a drawer, but similar collapse pattern)
  'openscad-camera-controls-collapsed':
    'openscad-forge-camera-controls-collapsed',
  'openscad-camera-controls-position':
    'openscad-forge-camera-controls-position',

  // ============================================================================
  // Drawer Size Keys -> openscad-drawer-{name}-width/height
  // ============================================================================
  'openscad-customizer-split-sizes': 'openscad-drawer-layout-sizes',

  // ============================================================================
  // App Preferences -> openscad-forge-{feature}
  // ============================================================================

  // Theme
  'openscad-customizer-theme': 'openscad-forge-theme',
  'openscad-customizer-high-contrast': 'openscad-forge-high-contrast',

  // Preview settings
  'openscad-customizer-measurements': 'openscad-forge-measurements',
  'openscad-customizer-grid': 'openscad-forge-grid',
  'openscad-customizer-auto-bed': 'openscad-forge-auto-bed',
  'openscad-customizer-auto-rotate': 'openscad-forge-auto-rotate',
  'openscad-customizer-rotate-speed': 'openscad-forge-rotate-speed',
  'openscad-customizer-model-color': 'openscad-forge-model-color',
  'openscad-customizer-status-bar': 'openscad-forge-status-bar',

  // Overlay settings (already mostly correct, just normalize)
  'openscad-overlay-enabled': 'openscad-forge-overlay-enabled',
  'openscad-overlay-opacity': 'openscad-forge-overlay-opacity',
  'openscad-overlay-source': 'openscad-forge-overlay-source',

  // Libraries
  'openscad-customizer-libraries': 'openscad-forge-libraries',

  // Performance/debug
  'openscad-perf-metrics': 'openscad-forge-perf-metrics',
  'openscad-metrics-log': 'openscad-forge-metrics-log',
  'openscad-lazy-union': 'openscad-forge-lazy-union',

  // State/draft
  'openscad-customizer-draft': 'openscad-forge-editor-draft',

  // Hidden feature mode (keep as-is since it's intentionally obscure)
  // 'openscad-customizer-hfm-unlock': 'openscad-customizer-hfm-unlock',

  // ============================================================================
  // Bare keys -> openscad-forge-{feature}
  // ============================================================================
  autoPreviewEnabled: 'openscad-forge-auto-preview-enabled',
  previewQualityMode: 'openscad-forge-preview-quality-mode',
  'recovery-source': 'openscad-forge-recovery-source',
  'recovery-timestamp': 'openscad-forge-recovery-timestamp',
  // Note: tutorialProgress is intentionally not migrated — the tutorial
  // stores progress in sessionStorage (see tutorial-sandbox.js), not
  // localStorage, so a localStorage migration would never find it.
};

/**
 * Keys that should not be migrated (already correct or intentionally different)
 */
export const PRESERVED_KEYS = [
  // Already follows spec
  'openscad-drawer-actions-state',

  // App preferences already using openscad-forge-*
  'openscad-forge-keyboard-shortcuts',
  'openscad-forge-feature-flags',
  'openscad-forge-user-id',
  'openscad-forge-first-visit-seen',
  'openscad-forge-storage-prefs',
  'openscad-forge-persistence-requested',
  'openscad-forge-manifold-engine',
  'openscad-forge-mode-prefs',

  // Presets have their own migration system
  'openscad-forge-presets-v2',
  'openscad-forge-presets-backup',
  'openscad-forge-migration-offered',
  'openscad-customizer-presets',
  'openscad-forge-presets-v1',

  // Grid size user presets (managed by preview.js)
  'openscad-forge-custom-grid-presets',

  // WASM crash-detection flags (set before/after WASM init; cleared on recovery)
  'openscad-forge-wasm-init-started',
  'openscad-forge-wasm-init-completed',

  // Saved projects use IndexedDB primarily
  'openscad-saved-projects',
  'openscad-saved-folders',
];

/**
 * Check if storage key migration has been run
 * @returns {boolean}
 */
export function hasMigrationRun() {
  const version = localStorage.getItem(MIGRATION_KEY);
  return version !== null && parseInt(version, 10) >= MIGRATION_VERSION;
}

/**
 * Run one-time localStorage key migration
 * Safely migrates legacy keys to new naming convention.
 * Old keys are removed only after successful copy.
 *
 * @param {Object} [options] - Migration options
 * @param {boolean} [options.dryRun=false] - If true, log changes but don't apply
 * @param {Function} [options.onMigrate] - Callback for each migrated key
 * @returns {Object} Migration results { migrated: string[], skipped: string[], errors: string[] }
 */
export function migrateStorageKeys(options = {}) {
  const { dryRun = false, onMigrate = null } = options;

  const results = {
    migrated: [],
    skipped: [],
    errors: [],
  };

  // Skip if already migrated
  if (!dryRun && hasMigrationRun()) {
    console.log('[StorageKeys] Migration already complete, skipping');
    return results;
  }

  console.log(
    `[StorageKeys] Running migration (v${MIGRATION_VERSION})${dryRun ? ' [DRY RUN]' : ''}`
  );

  for (const [oldKey, newKey] of Object.entries(KEY_MIGRATIONS)) {
    try {
      const oldValue = localStorage.getItem(oldKey);

      if (oldValue === null) {
        // Key doesn't exist, skip
        results.skipped.push(oldKey);
        continue;
      }

      // Check if new key already exists (don't overwrite)
      const newValue = localStorage.getItem(newKey);
      if (newValue !== null) {
        console.log(
          `[StorageKeys] Skipping ${oldKey} -> ${newKey} (new key already exists)`
        );
        results.skipped.push(oldKey);
        continue;
      }

      if (!dryRun) {
        // Copy value to new key
        localStorage.setItem(newKey, oldValue);

        // Verify copy succeeded
        if (localStorage.getItem(newKey) === oldValue) {
          // Remove old key only after successful copy
          localStorage.removeItem(oldKey);
          results.migrated.push(oldKey);

          if (onMigrate) {
            onMigrate(oldKey, newKey, oldValue);
          }
        } else {
          throw new Error('Copy verification failed');
        }
      } else {
        console.log(`[StorageKeys] Would migrate: ${oldKey} -> ${newKey}`);
        results.migrated.push(oldKey);
      }
    } catch (error) {
      console.error(`[StorageKeys] Error migrating ${oldKey}:`, error);
      results.errors.push(`${oldKey}: ${error.message}`);
    }
  }

  // Mark migration as complete
  if (!dryRun && results.errors.length === 0) {
    localStorage.setItem(MIGRATION_KEY, String(MIGRATION_VERSION));
    console.log(
      `[StorageKeys] Migration complete: ${results.migrated.length} keys migrated, ${results.skipped.length} skipped`
    );
  }

  return results;
}

/**
 * Get the standardized key name for a storage key
 * Returns the new key if a migration exists, otherwise returns the original
 *
 * @param {string} key - The legacy or new key name
 * @returns {string} The standardized key name
 */
export function getStandardKey(key) {
  return KEY_MIGRATIONS[key] || key;
}

/**
 * Helper to generate drawer state key
 * @param {string} drawerName - Name of the drawer (e.g., 'parameters', 'camera')
 * @returns {string} Standardized drawer state key
 */
export function getDrawerStateKey(drawerName) {
  return `openscad-drawer-${drawerName}-state`;
}

/**
 * Helper to generate drawer size key
 * @param {string} drawerName - Name of the drawer
 * @param {'width'|'height'} dimension - Size dimension
 * @returns {string} Standardized drawer size key
 */
export function getDrawerSizeKey(drawerName, dimension) {
  return `openscad-drawer-${drawerName}-${dimension}`;
}

/**
 * Helper to generate app preference key
 * @param {string} feature - Feature name (e.g., 'theme', 'auto-preview')
 * @returns {string} Standardized app preference key
 */
export function getAppPrefKey(feature) {
  return `openscad-forge-${feature}`;
}

// ============================================================================
// Centralized STORAGE_KEY_* constants
// ============================================================================
// Key strings must never change — renaming a key orphans users' saved data.
// tests/unit/storage-keys.test.js snapshots every exported key value to
// guard against accidental renames.
// ============================================================================

// --- App preferences (main.js) ---
export const STORAGE_KEY_AUTO_PREVIEW_ENABLED = getAppPrefKey(
  'auto-preview-enabled'
);
export const STORAGE_KEY_PREVIEW_QUALITY = getAppPrefKey(
  'preview-quality-mode'
);
export const STORAGE_KEY_RECOVERY_SOURCE = getAppPrefKey('recovery-source');
export const STORAGE_KEY_RECOVERY_TIMESTAMP =
  getAppPrefKey('recovery-timestamp');
export const STORAGE_KEY_STATUS_BAR = getAppPrefKey('status-bar');
export const STORAGE_KEY_MODEL_COLOR = getAppPrefKey('model-color');
export const STORAGE_KEY_MODEL_COLOR_ENABLED = getAppPrefKey(
  'model-color-enabled'
);
export const STORAGE_KEY_MODEL_OPACITY = getAppPrefKey('model-opacity');
export const STORAGE_KEY_BRIGHTNESS = getAppPrefKey('brightness');
export const STORAGE_KEY_CONTRAST = getAppPrefKey('contrast');
export const STORAGE_KEY_MODEL_APPEARANCE_ENABLED = getAppPrefKey(
  'model-appearance-enabled'
);
export const STORAGE_KEY_PARAM_PANEL_COLLAPSED =
  getDrawerStateKey('parameters');
export const STORAGE_KEY_LAYOUT_SIZES = getAppPrefKey('layout-sizes');
// The Customizer on a phone held upright: its width as a share of the
// screen's width, and how solid it is (Preferences > 3D View).
export const STORAGE_KEY_PHONE_CUSTOMIZER_WIDTH = getAppPrefKey(
  'phone-customizer-width'
);
export const STORAGE_KEY_PHONE_CUSTOMIZER_OPACITY = getAppPrefKey(
  'phone-customizer-opacity'
);
// Preview Settings & Info on a phone held upright: the sheet's height as a
// share of the screen's height.
export const STORAGE_KEY_PHONE_SETTINGS_HEIGHT = getAppPrefKey(
  'phone-settings-height'
);
// The game controller's preset, its family and a person's own assignments
// (Keys > Controller); one device, so one copy for every interface.
export const STORAGE_KEY_GAMEPAD_MAPPING = getAppPrefKey('gamepad-mapping');
// The game's own table of controller buttons, kept apart from the app's.
export const STORAGE_KEY_GAMEPAD_WALK_MAPPING = getAppPrefKey(
  'gamepad-walk-mapping'
);
// How a person holds one Joy-Con, and uses the two together (Keys >
// Controller, asked once when they first connect).
export const STORAGE_KEY_JOYCON = getAppPrefKey('joycon');
// A person's own named controller presets (Keys > Controller), each a
// copy of the app's table.
export const STORAGE_KEY_GAMEPAD_PRESETS = getAppPrefKey('gamepad-presets');

// --- Preview settings (preview.js) ---
export const STORAGE_KEY_MEASUREMENTS = getAppPrefKey('measurements');
export const STORAGE_KEY_GRID = getAppPrefKey('grid');
export const STORAGE_KEY_GRID_SIZE = getAppPrefKey('grid-size');
export const STORAGE_KEY_CUSTOM_GRID_PRESETS = getAppPrefKey(
  'custom-grid-presets'
);
export const STORAGE_KEY_GRID_COLOR = getAppPrefKey('grid-color');
export const STORAGE_KEY_GRID_OPACITY = getAppPrefKey('grid-opacity');
export const STORAGE_KEY_AUTO_BED = getAppPrefKey('auto-bed');
export const STORAGE_KEY_ZOOM_TO_CURSOR = getAppPrefKey('zoom-to-cursor');
export const STORAGE_KEY_VIEWPORT_SCHEME = getAppPrefKey('viewport-scheme');

// --- Code editor settings (editor-prefs.js, Preferences ▸ Editor) ---
// The font-size key predates the dialog: edit-actions-controller has always
// used it for Edit ▸ Increase/Decrease Font Size. Both import it from here
// so the menu and the dialog cannot drift onto separate values.
export const STORAGE_KEY_EDITOR_FONT_SIZE = getAppPrefKey('editor-font-size');
export const STORAGE_KEY_EDITOR_INDENT_WIDTH = getAppPrefKey(
  'editor-indent-width'
);
export const STORAGE_KEY_EDITOR_TAB_WIDTH = getAppPrefKey('editor-tab-width');
export const STORAGE_KEY_EDITOR_LINE_WRAP = getAppPrefKey('editor-line-wrap');
export const STORAGE_KEY_EDITOR_HIGHLIGHT_LINE = getAppPrefKey(
  'editor-highlight-line'
);
// The two wrap marks are separate keys because the desktop keeps them as two
// settings, mirrored here (lineWrapIndentationStyle and
// lineWrapVisualizationEnd, settings.cc).
export const STORAGE_KEY_EDITOR_WRAP_INDENT =
  getAppPrefKey('editor-wrap-indent');
export const STORAGE_KEY_EDITOR_WRAP_ARROW = getAppPrefKey('editor-wrap-arrow');
// settings.cc: Settings::enableBraceMatching, default true, sitting beside
// highlightCurrentLine — which this app already exposes as a live toggle.
export const STORAGE_KEY_EDITOR_BRACE_MATCHING = getAppPrefKey(
  'editor-brace-matching'
);
// The desktop editor has an on-off switch for autocompletion. A popup on
// every keystroke can get in a screen reader's way, so it is a real one here.
export const STORAGE_KEY_EDITOR_AUTOCOMPLETE = getAppPrefKey(
  'editor-autocomplete'
);
export const STORAGE_KEY_CAMERA_COLLAPSED = getAppPrefKey(
  'camera-controls-collapsed'
);
export const STORAGE_KEY_CAMERA_POSITION = getAppPrefKey(
  'camera-controls-position'
);
export const STORAGE_KEY_LOD_WARNING_DISMISSED = getAppPrefKey(
  'lod-warning-dismissed'
);

// --- Render metrics and engine toggles (render/auto-preview controllers) ---
export const STORAGE_KEY_PERF_METRICS = getAppPrefKey('perf-metrics');
export const STORAGE_KEY_METRICS_LOG = getAppPrefKey('metrics-log');
export const STORAGE_KEY_LAZY_UNION = getAppPrefKey('lazy-union');
export const STORAGE_KEY_MANIFOLD_ENGINE = getAppPrefKey('manifold-engine');
// Preferences ▸ Advanced ▸ Play sounds; on unless set to 'false'.
export const STORAGE_KEY_SOUNDS = getAppPrefKey('sounds');
// Preferences ▸ Advanced ▸ Sounds in Alt View: the kinds turned off, a JSON
// list of their names (hfm-controller.js); none unless set.
export const STORAGE_KEY_SOUND_KINDS_OFF = getAppPrefKey('sound-kinds-off');
// Preferences ▸ Advanced ▸ Engine: the chosen engine's id in
// public/wasm/engines.json (engine-choice.js); the Forge engine unless set.
export const STORAGE_KEY_ENGINE = getAppPrefKey('engine');

// --- UI mode (ui-mode-controller.js) ---
// The controller has always persisted under this name; centralized here so
// ui-scoped-prefs.js can resolve the active interface without importing the
// controller. The string itself is unchanged.
export const STORAGE_KEY_UI_MODE = 'openscad-forge-ui-mode';

// --- UI-scoped preference split marker (ui-scoped-prefs.js) ---
// Gates the one-time seeding of the per-interface namespaces.
export const STORAGE_KEY_SCOPED_PREFS_SEEDED = getAppPrefKey(
  'scoped-prefs-seeded-v1'
);

// --- Persistent tutorial registry (tutorial-sandbox.js) ---
// App-level: which tutorial families were ever
// opened/completed/dismissed. Distinct from the sessionStorage step
// progress ('tutorialProgress'), which stays session-scoped by design.
export const STORAGE_KEY_TUTORIAL_STATE = getAppPrefKey('tutorial-state');

// --- Welcome tour nudge, permanent suppression (tour-nudge.js) ---
// App-level for the same reason as the registry above: whether someone wants
// to be offered the welcome tour is a fact about the person, not about which
// interface they happen to be in, and the tour itself is one family across
// Forge and Classic. Written only by the modal's "Do not show this again"
// checkbox; the card tip's own dismissal is recorded in the registry
// instead, so the two controls keep separate scopes.
export const STORAGE_KEY_TOUR_NUDGE_SUPPRESSED = getAppPrefKey(
  'tour-nudge-suppressed'
);

// --- Preset dropdown sort order ---
export const PRESET_SORT_KEY = 'openscad-forge-preset-sort';

// --- WASM crash-detection flags (set before/after init; cleared on recovery) ---
export const STORAGE_KEY_WASM_INIT_STARTED = 'openscad-forge-wasm-init-started';
export const STORAGE_KEY_WASM_INIT_COMPLETED =
  'openscad-forge-wasm-init-completed';

// ============================================================================
// Safe localStorage access
// ============================================================================
// localStorage throws in several legitimate situations (private browsing,
// storage quota exceeded, third-party-cookie lockdown). These wrappers give
// call sites one consistent, non-throwing behavior with an audit trail via
// console.warn instead of dozens of ad-hoc try/catch blocks.
// ============================================================================

/**
 * Read a localStorage value without throwing.
 *
 * @param {string} key - Storage key
 * @param {string|null} [fallback=null] - Returned when the key is absent or
 *   storage is unavailable
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - Suppress the console.warn (for
 *   hot paths where a warn per read would be noisy)
 * @returns {string|null}
 */
export function safeGetItem(key, fallback = null, { silent = false } = {}) {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch (error) {
    if (!silent) {
      console.warn(`[Storage] Failed to read ${key}:`, error);
    }
    return fallback;
  }
}

/**
 * Write a localStorage value without throwing.
 *
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - Suppress the console.warn
 * @returns {boolean} True when the write succeeded
 */
export function safeSetItem(key, value, { silent = false } = {}) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!silent) {
      console.warn(`[Storage] Failed to write ${key}:`, error);
    }
    return false;
  }
}

/**
 * Remove a localStorage key without throwing.
 *
 * @param {string} key - Storage key
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - Suppress the console.warn
 * @returns {boolean} True when the removal succeeded
 */
export function safeRemoveItem(key, { silent = false } = {}) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    if (!silent) {
      console.warn(`[Storage] Failed to remove ${key}:`, error);
    }
    return false;
  }
}

// ============================================================================
// Developer debug toggles
// ============================================================================

/**
 * Developer debug-toggle keys. Presence-based: storing any value under a
 * key activates the toggle; removing the key deactivates it. The key
 * strings are documented in
 * docs/archive/audit/ki-012-investigation/capture-protocol.md and must not
 * change.
 */
export const DEBUG_PREFS = Object.freeze({
  previewParity: 'openscad-forge-debug-preview-parity',
  desktopQuality: 'openscad-forge-debug-desktop-quality',
  noCsgColors: 'openscad-forge-debug-no-csg-colors',
  sourceOverrides: 'openscad-forge-debug-source-overrides',
});

/**
 * Whether a developer debug toggle is active.
 *
 * @param {'previewParity'|'desktopQuality'|'noCsgColors'|'sourceOverrides'} name
 * @returns {boolean} True when any value is stored under the toggle's key
 */
export function isDebugPrefEnabled(name) {
  const key = DEBUG_PREFS[name];
  if (!key) return false;
  return (
    typeof localStorage !== 'undefined' && localStorage.getItem(key) !== null
  );
}

// ============================================================================
// HFM (Alt View) persistent settings
// ============================================================================

/**
 * The Lens of each look, one record a look: the phosphor looks (green and
 * amber) and Classic's DOS look. Each value is a JSON string
 * `{ "edges": n, "focus": n, "glow": n, "surfaces": n }`, decimals within
 * the ranges in hfm-controller.js; a field left out starts where the look
 * starts.
 */
export const STORAGE_KEY_HFM_LENS_PHOSPHOR = 'openscad-forge-hfm-lens-phosphor';
export const STORAGE_KEY_HFM_LENS_DOS = 'openscad-forge-hfm-lens-dos';

/**
 * The four keys the versions before 5.7.0 wrote, one a value, as decimal
 * strings. They are read once into the phosphor record and removed, and
 * stay here as names for that migration only.
 */
export const STORAGE_KEY_HFM_CONTRAST_SCALE =
  'openscad-forge-hfm-contrast-scale';
export const STORAGE_KEY_HFM_FONT_SCALE = 'openscad-forge-hfm-font-scale';
export const STORAGE_KEY_HFM_PERSIST_FADE = 'openscad-forge-hfm-persist-fade';
export const STORAGE_KEY_HFM_SURFACES = 'openscad-forge-hfm-surfaces';

/**
 * localStorage key for the grid while the Alt View is on: 'true' only after
 * a person turns the grid on there; absent means hidden. One key for both
 * interfaces, apart from the ordinary per-interface grid preference.
 */
export const STORAGE_KEY_HFM_GRID = getAppPrefKey('hfm-grid');

/**
 * localStorage key for how many times the Alt View's code has been found in
 * this browser: JSON { entries, permanent, pendingBreak }, written by
 * alt-unlock-stages.js and never expired. The key stays from the third
 * finding on.
 */
export const STORAGE_KEY_ALT_UNLOCK = getAppPrefKey('alt-unlock');

/**
 * localStorage key for the City Walk game's walking-speed multiplier.
 * Decimal string in [0.5, 3]; a comfort/accessibility preference,
 * so it persists across sessions.
 */
export const STORAGE_KEY_CITY_WALK_SPEED = getAppPrefKey('city-walk-speed');

/**
 * localStorage key for the City Walk game's ASCII character scale.
 * Decimal string on the game's own 10-point grid in [0.1, 1]; kept separate
 * from STORAGE_KEY_HFM_FONT_SCALE because the game's range goes far smaller
 * than the preview Alt View slider's 0.5 floor. A comfort/accessibility
 * preference, so it persists across sessions.
 */
export const STORAGE_KEY_CITY_WALK_FONT_SCALE = getAppPrefKey(
  'city-walk-font-scale'
);

/**
 * localStorage key for the City Walk game's calibrated character-size
 * floor, from the last entry calibration on this machine: a candidate
 * scale ('0.1' or '0.3', floor and landing default alike) or 'fallback'
 * (nothing in range held 30 fps — floor 0.3, default stays 0.5). Written by
 * calibration only, never by the player; the player's own choice lives in
 * STORAGE_KEY_CITY_WALK_FONT_SCALE, whose presence means a manual choice
 * that calibration must never override.
 */
export const STORAGE_KEY_CITY_WALK_CALIBRATED_FLOOR = getAppPrefKey(
  'city-walk-calibrated-floor'
);

/**
 * localStorage key for the City Walk game's colour/monochrome choice.
 * 'on' or 'off'. Absent is meaningful and is the default: with no stored
 * value, color follows high contrast. The key is written only when the
 * player works
 * the Color toggle themselves, and from then on their choice wins.
 */
export const STORAGE_KEY_CITY_WALK_COLOUR = getAppPrefKey('city-walk-colour');

/**
 * localStorage key for the City Walk's Day/Night choice: 'day' or
 * 'night'. Absent means night: characters on the page's own black, and
 * nothing behind them.
 *
 * Day fills the black gaps on nearby surfaces with a dark material tint
 * under the glyphs. It changes no glyph and no color decision, only what
 * is behind them.
 */
export const STORAGE_KEY_CITY_WALK_DAYLIGHT =
  getAppPrefKey('city-walk-daylight');

/**
 * localStorage key for the City Walk's mouse-look preference: 'follow'
 * (the view turns toward the cursor, the default), 'drag' (a held-button
 * drag), or 'off'. Absent means follow, except under
 * prefers-reduced-motion where absent means off; a stored choice always
 * wins over both defaults.
 */
export const STORAGE_KEY_CITY_WALK_LOOK = getAppPrefKey('city-walk-look');

/**
 * localStorage key for the City Walk's empty-city choice: 'on' means the
 * streets are empty. Absent means the city is populated, the default.
 *
 * It hides the people and the cars and takes their obstacles out of the
 * collision grid, because a city you can walk through has to be a city you
 * can walk through: an invisible car you bump into is worse than a visible
 * one.
 */
export const STORAGE_KEY_CITY_WALK_EMPTY_CITY = getAppPrefKey(
  'city-walk-empty-city'
);

/**
 * localStorage key for whether the City Walk's Camera panel is collapsed:
 * 'true' or 'false'.
 *
 * It is a drawer key rather than an app preference because that is what it
 * is, and it is scoped to the game rather than sharing the preview's
 * 'camera' drawer: the two panels look alike and do the same job, but one is
 * beside a model and the other beside a city, and someone who keeps the
 * preview's collapsed has said nothing about the game's.
 */
export const STORAGE_KEY_CITY_WALK_CAMERA_PANEL =
  getDrawerStateKey('camera-city-walk');

/**
 * localStorage key for the City Walk map's drawing style.
 * One of 'standard', 'roads', 'buildings', 'wayfinding'.
 *
 * Absent means Standard, the default. An unrecognised value also falls
 * back to Standard rather than to nothing: a style is how the map is
 * drawn, and a map that failed to draw would be a worse answer than a
 * plain one.
 */
export const STORAGE_KEY_CITY_WALK_MAP_STYLE = getAppPrefKey(
  'city-walk-map-style'
);

/**
 * What a player has found in one city.
 *
 * A key per city, because progress is per city: Seattle's landmarks say
 * nothing about Denver's, and a single key would either mix them or need a
 * shape that is a per-city map wearing one name.
 *
 * The value is a JSON object on purpose, not a bare list: any other
 * per-city fact (fireworks unlocked, a traveler met) belongs in this
 * object rather than in a sibling key, because three keys per city is
 * three chances for them to disagree about the same visit. Unknown fields
 * are preserved on write for the same reason: an older build must not eat
 * a newer one's progress.
 *
 * The store's known limit: landmark names are the identity. A rebake that
 * renames a landmark orphans its tick, and the player is silently
 * un-visited there. Names were chosen anyway because the alternative, an
 * index into the landmark list, breaks on any rebake that adds or drops
 * one, which is far more common.
 *
 * @param {string} citySlug the city's own slug, e.g. 'seattle'
 */
export function getCityWalkProgressKey(citySlug) {
  return getAppPrefKey(`city-walk-progress-${citySlug}`);
}
